import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { NODE_DEFS, ACCENT_COLORS } from '../../data/nodeDefinitions';
import Icon from '../ui/Icon';
import styles from './JourneyNode.module.css';

function nodeBodyText(type, cfg) {
  if (!cfg) return '';
  switch (type) {
    case 'entry-event':     return cfg.event || '—';
    case 'entry-schedule':  return cfg.preset || cfg.cron || 'Custom cron';
    case 'entry-segment':   return cfg.segmentName || cfg.segmentId || 'Pick a segment';
    case 'entry-api':       return cfg.description || 'API Trigger';
    case 'action-push':     return cfg.title || 'No title set';
    case 'action-whatsapp': return cfg.isCustomText ? (cfg.text ? cfg.text.slice(0, 30) + '…' : 'No message') : (cfg.templateName || 'No template');
    case 'action-email':    return cfg.subject || 'No subject';
    case 'action-sms':      return cfg.text ? cfg.text.slice(0, 30) + '…' : 'No message';
    case 'action-rcs':      return cfg.templateId || 'No template';
    case 'action-webhook':  return cfg.url || 'No URL';
    case 'wait-relative':   return 'Wait ' + (cfg.amount || '1') + ' ' + (cfg.unit || 'hours');
    case 'wait-absolute':   return cfg.rule === 'next_day_noon' ? 'Tomorrow 12 PM' : (cfg.rule || '—');
    case 'logic-condition': {
      const labels = { if_not_opened: "Hasn't opened previous step", if_delivered: 'Previous step delivered', if_failed: 'Previous step failed' };
      return labels[cfg.condType] || cfg.condType || '—';
    }
    case 'logic-ab':        return (cfg.splitA || '50') + '% / ' + (cfg.splitB || '50') + '%';
    case 'logic-goal':      return cfg.event || '—';
    case 'logic-exit':      return 'End of journey';
    default:                return '';
  }
}

// Named source handles are spread evenly along the bottom edge so branch
// labels (Yes/No, A/B) line up visually with their own connector.
function outHandleStyle(index, total) {
  if (total <= 1) return undefined;
  const pct = ((index + 1) / (total + 1)) * 100;
  return { left: pct + '%' };
}

function JourneyNode({ id, data, selected }) {
  const { nodeType, cfg, onDelete } = data;
  const def = NODE_DEFS[nodeType] || {};
  const accentColor = ACCENT_COLORS[def.accentType] || '#888';
  const body = nodeBodyText(nodeType, cfg);
  const handles = def.handles || { in: true, out: ['out'] };

  return (
    <div
      className={[styles.node, selected ? styles.selected : '', def.comingSoon ? styles.comingSoon : ''].join(' ')}
      style={{ '--accent': accentColor }}
    >
      {handles.in && <Handle type="target" position={Position.Top} className={styles.handle} />}

      <div className={styles.header} style={{ borderBottom: '1px solid ' + accentColor + '30' }}>
        <div className={styles.ico} style={{ background: def.icoC, color: def.icoT }}>
          <Icon name={def.ico} size={16} />
        </div>
        <div className={styles.label}>{def.label}</div>
        {def.comingSoon && <span className={styles.soonBadge} title="The journey engine doesn't run this step type yet — safe to plan with, won't execute on publish.">Soon</span>}
        <button
          className={styles.del}
          onClick={e => { e.stopPropagation(); onDelete && onDelete(id); }}
        ><span className="material-symbols-rounded" style={{ fontSize: 13 }}>close</span></button>
      </div>

      {body && (
        <div className={styles.body}>
          <div className={styles.bodyText}>{body}</div>
        </div>
      )}

      {def.hasBranches && def.branches && (
        <div className={styles.branches}>
          {def.branches.map((b, i) => (
            <div key={i} className={[styles.branch, styles['branch-' + def.branchStyles[i]]].join(' ')}>
              {b}
            </div>
          ))}
        </div>
      )}

      {handles.out.map((handleId, i) => (
        <Handle
          key={handleId}
          id={handleId}
          type="source"
          position={Position.Bottom}
          className={styles.handle}
          style={outHandleStyle(i, handles.out.length)}
        />
      ))}
    </div>
  );
}

export default memo(JourneyNode);
